/**
 * The engine "physics" constants for the client-side responsive-image library.
 *
 * ## What lives here (and what no longer does)
 *
 * `LADDER`/`QUALITY`/`DPR` are the stable engine constants the builder needs —
 * the srcset width ladder, the Cloudflare quality, and the `fixed`-mode DPR
 * steps. They are a small, hand-kept mirror of the same three constants in
 * `ResponsiveImageConfig.php`, still drift-gated by a consts-only cross-seam test
 * (`engine-consts-mirror.test.ts`). These are "physics": they change rarely and
 * the byte-identical CF-URL form depends on them, so a test gate is the right
 * tool.
 *
 * The PER-REGION mirror (`REGIONS`/`makeRegion`/`NamedRegion`) USED to live here —
 * a hand-kept copy of the `datte-ipl-image` row's `mode`/`maxWidth`/`sizes`. It is
 * GONE: the server now provides each region's spec to the JS at runtime via a
 * `data-rimg-spec` DOM attribute ({responsiveImageSpec}, PHP SSoT), so a consumer
 * reads the spec instead of a JS copy. Per-region drift is therefore IMPOSSIBLE
 * (not merely detectable), which is strictly better than the old detect-only test
 * — see the tab template + product-attribute-description.ts. (ADR-supersedes the
 * ADR-0014 `REGIONS` decision; ADR-0013's CSS drift-lint is untouched — the
 * `datte-ipl-image` row never had a `cssFrame`, so it was never lint-guarded.)
 *
 * Side-effect-free: pure constant data, no DOM, no auto-init.
 *
 * @see src/Services/ResponsiveImage/ResponsiveImageConfig.php (the SSoT for the consts)
 * @see tests/unit/responsive-image/engine-consts-mirror.test.ts (the consts drift gate)
 * @see src/productdetails/ipl-compat-tab.tpl (emits the per-region spec the JS now reads)
 * @see docs/decisions/0013-render-size-ssot-and-drift-lint.md
 */

/** ResponsiveImageConfig::LADDER — the fixed srcset width ladder (drift-gated). */
export const LADDER = [320, 480, 640, 768, 1024, 1280, 1536, 1920] as const;

/** ResponsiveImageConfig::QUALITY — Cloudflare quality parameter (drift-gated). */
export const QUALITY = 80 as const;

/** ResponsiveImageConfig::DPR — device-pixel-ratio steps for `fixed`-mode x-descriptors (drift-gated). */
export const DPR = [1, 2, 3] as const;
